// apps/web/src/features/russia-requests/components/editor/RussiaEditorBody.tsx
//
// Form body for the russia-rates manual editor. Lives under
// `<EditorDrawer>` so it can consume `useEditorGuard()` for the close
// affordance and the post-create bypass.
//
// @aiInvariant `bypassNext()` must run BEFORE `navigate(...)` on submit
//   success so the still-dirty RHF state does not intercept the
//   same-tx navigation. The host owns the navigate; this file owns the
//   ordering.

import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import {
  CreateRussiaRequestManualInputSchema,
  RussiaRequestKind,
  type CreateRussiaRequestManualInput,
  type PatchRussiaRequestInput,
  type RussiaRequest,
} from '@magimp/shared';
import { IconButton, useEditorGuard } from '../../../../ui/index.js';
import { useToastStore } from '../../../../stores/toast.js';
import { patchRussiaRequest } from '../../../../api/russia-requests/crud.js';
import { russiaRequestsKeys, useCreateRussiaRequestManual } from '../../queries.js';
import { RussiaEditorBasicsCard } from './RussiaEditorBasicsCard.js';
import { RussiaEditorEndAddressCard } from './RussiaEditorEndAddressCard.js';
import { RussiaEditorStartCitiesCard } from './RussiaEditorStartCitiesCard.js';
import { RussiaEditorPositionsCard } from './RussiaEditorPositionsCard.js';
import { RussiaEditorTotalsCard } from './RussiaEditorTotalsCard.js';
import { RussiaEditorFilesCard } from './RussiaEditorFilesCard.js';
import { RussiaEditorFooter } from './RussiaEditorFooter.js';
import { useRussiaEditorAutosave } from './useRussiaEditorAutosave.js';
import type { RussiaPositionRow } from './russiaPositions.model.js';

export interface RussiaEditorBodyProps {
  form: UseFormReturn<CreateRussiaRequestManualInput>;
  /** Create-time complexity discriminator. SIMPLE shows the positions card. */
  kind: RussiaRequestKind;
  /** Switches the title / save copy to the edit-an-existing-draft variant. */
  editMode: boolean;
  persistedId: string | null;
  setPersistedId: (id: string) => void;
  /** Already-persisted SIMPLE positions to seed in edit mode (#1571). */
  initialPositions?: RussiaPositionRow[] | undefined;
  submitting: boolean;
  setSubmitting: (busy: boolean) => void;
  onClose: () => void;
  onNavigateToRow: (id: string) => void;
  /** Surfaces the positions card's unsaved-work signal so the host can fold
   *   it into the drawer's unsaved-changes guard (#1545 round-6
   *   COD-REV-001). */
  onPositionsDirtyChange?: ((dirty: boolean) => void) | undefined;
  /** Hands the host the positions card's never-saved-photo-key collector so
   *   it can GC orphaned upload objects on a confirmed discard-close (#1561). */
  onRegisterCollectNeverSavedPhotoKeys?: ((collect: () => Promise<string[]>) => void) | undefined;
}

export function RussiaEditorBody(props: RussiaEditorBodyProps) {
  const {
    form,
    kind,
    editMode,
    persistedId,
    setPersistedId,
    initialPositions,
    submitting,
    setSubmitting,
    onClose,
    onNavigateToRow,
    onPositionsDirtyChange,
    onRegisterCollectNeverSavedPhotoKeys,
  } = props;
  const { t } = useTranslation('russiaRequests');
  const { bypassNext, confirmDestructive } = useEditorGuard();
  const showToast = useToastStore((s) => s.show);

  const ensurePersistInFlight = useRef<Promise<string | null> | null>(null);
  const flushPositionsRef = useRef<((parentId: string) => Promise<void>) | null>(null);

  const createMutation = useCreateRussiaRequestManual();
  const qc = useQueryClient();

  const create = useCallback(
    (input: CreateRussiaRequestManualInput) => createMutation.mutateAsync(input),
    [createMutation],
  );
  // WHY (Codex RUS-941-004): mirror the invalidation that
  //   `usePatchRussiaRequest` performs on success — the list and the
  //   detail cache must reflect the autosaved snapshot, otherwise the
  //   list behind the drawer keeps showing the first POST'd version
  //   until another invalidator runs.
  const patch = useCallback(
    async (id: string, input: CreateRussiaRequestManualInput): Promise<RussiaRequest> => {
      // WHY (#1543): `kind` is a create-only discriminator — it is NOT in the
      //   strict PatchRussiaRequestInputSchema, so forwarding the raw create
      //   input would make every post-autosave PATCH 400. Strip it here.
      const { kind: _kind, ...patchable } = input;
      const body: PatchRussiaRequestInput = patchable;
      const row = await patchRussiaRequest(id, body);
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.all });
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.detail(id) });
      return row;
    },
    [qc],
  );

  const {
    state: autosave,
    notifyChanged,
    cancelPending,
  } = useRussiaEditorAutosave({
    form,
    persistedId,
    create,
    patch,
    onPersisted: setPersistedId,
    onError: (text) => showToast({ tone: 'error', text }),
  });

  // WHY: with `mode: 'all'` RHF's `isValid` updates reactively after
  //   the first change. The host bridges to `formState.isValid` only —
  //   calling `form.watch()` in render here would resubscribe on
  //   every keystroke and race userEvent typing (silent char loss).
  // Codex RUS-941-001 + RUS-941-006: also gate on ANY autosave being
  //   in flight — the POST case would double-create rows (non-
  //   idempotent endpoint), and the PATCH case would let a stale
  //   autosave PATCH win the lost-update race against the explicit
  //   Save PATCH (backend writes whatever scalar arrives last).
  const blockedByInFlightSave = autosave.status === 'saving';
  const canSubmit = !submitting && form.formState.isValid && !blockedByInFlightSave;

  // WHY (#1545): positions attach to a saved DRAFT id. Before the first
  //   position POST the card calls this to materialize that id — it owns
  //   the create explicitly (cancelling any debounced autosave first so a
  //   stray autosave create cannot double-POST the non-idempotent manual
  //   endpoint) when the form is valid; otherwise it returns null and the
  //   card skips the POST until the header is fillable.
  const ensurePersisted = useCallback(async (): Promise<string | null> => {
    if (persistedId !== null) return persistedId;
    if (ensurePersistInFlight.current !== null) return ensurePersistInFlight.current;
    const values = form.getValues();
    const parsed = CreateRussiaRequestManualInputSchema.safeParse(values);
    if (!parsed.success) return null;
    cancelPending();
    const run = (async (): Promise<string | null> => {
      try {
        const row = await create(parsed.data);
        setPersistedId(row.id);
        return row.id;
      } catch (err) {
        showToast({ tone: 'error', text: err instanceof Error ? err.message : String(err) });
        return null;
      } finally {
        ensurePersistInFlight.current = null;
      }
    })();
    ensurePersistInFlight.current = run;
    return run;
  }, [persistedId, form, cancelPending, create, setPersistedId, showToast]);

  const requestClose = () => confirmDestructive(onClose);

  const onSubmit = form.handleSubmit(async (data) => {
    setSubmitting(true);
    // Codex RUS-941-003: drop any debounced timer / pending coalesced
    //   snapshot BEFORE we own the POST/PATCH ourselves, otherwise a
    //   stray autosave create could fire mid-explicit-submit and
    //   double-POST the non-idempotent manual-create endpoint.
    cancelPending();
    try {
      let id = persistedId;
      if (id === null) {
        id = await ensurePersisted();
        if (id === null) return; // header not materializable (form invalid) — should not happen post-validation
      } else {
        // WHY: a prior autosave may have persisted earlier state; PATCH
        //   with the current snapshot so the explicit Save click
        //   reflects the user's final input, even if autosave already
        //   wrote a partial version of it.
        await patch(id, data);
      }
      // Persist positions entered before the parent existed, before leaving.
      await flushPositionsRef.current?.(id);
      bypassNext();
      onNavigateToRow(id);
    } catch (err) {
      showToast({ tone: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <form
      onSubmit={onSubmit}
      data-testid="russia-requests-editor"
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-1)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
          {t(editMode ? 'editor.editTitle' : 'editor.title')}
        </h2>
        <IconButton
          icon="x"
          label={t('editor.cancel')}
          onClick={requestClose}
          data-testid="russia-requests-editor-close"
        />
      </div>
      <div style={{ flex: 1, padding: '16px 20px', overflow: 'auto' }}>
        <RussiaEditorBasicsCard form={form} onChanged={notifyChanged} />
        <RussiaEditorEndAddressCard form={form} onChanged={notifyChanged} />
        <RussiaEditorStartCitiesCard form={form} onChanged={notifyChanged} />
        {/* WHY (#1546): SIMPLE and COMPLEX are mutually exclusive, mirroring
            the backend. SIMPLE shows the per-position editor; COMPLEX shows
            request-level totals + file attachments and NO positions. */}
        {kind === RussiaRequestKind.SIMPLE ? (
          <RussiaEditorPositionsCard
            persistedId={persistedId}
            ensurePersisted={ensurePersisted}
            initialRows={initialPositions}
            onError={(text) => showToast({ tone: 'error', text })}
            registerFlush={(fn) => {
              flushPositionsRef.current = fn;
            }}
            registerCollectNeverSavedPhotoKeys={onRegisterCollectNeverSavedPhotoKeys}
            onDirtyChange={onPositionsDirtyChange}
          />
        ) : (
          <>
            <RussiaEditorTotalsCard form={form} onChanged={notifyChanged} />
            <RussiaEditorFilesCard persistedId={persistedId} ensurePersisted={ensurePersisted} />
          </>
        )}
      </div>
      <RussiaEditorFooter
        autosave={autosave}
        canSubmit={canSubmit}
        submitting={submitting}
        editMode={editMode}
        onCancel={requestClose}
      />
    </form>
  );
}
